import { useState, useEffect } from "react";
import api, { apiErr } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, AlertTriangle } from "lucide-react";

const REGIONS = ["US", "CA", "GCC", "IN"];
const STATUSES = ["new", "active", "screening", "submitted", "interviewing", "offered", "placed", "on_hold"];
const REGION_CCY = { US: "USD", CA: "CAD", GCC: "AED", IN: "INR" };

const EMPTY = {
  full_name: "", primary_email: "", phones: "", current_title: "", current_employer: "",
  location: "", region: "US", currency: "USD", years_experience: "", skills: "",
  work_authorization: "", source: "", status: "new", screening_notes: "",
  current_compensation: "", expected_compensation: "",
  notice_period: "", availability: "", interview_availability: "", time_zone: "",
  willing_to_relocate: false, willing_to_travel: false, certifications: "",
  consent_status: "pending", consent_date: "", consent_method: "", consent_source: "",
};

export default function CandidateForm({ open, onClose, onSaved, candidate }) {
  const isEdit = !!candidate;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [dups, setDups] = useState([]);

  useEffect(() => {
    if (candidate) {
      setForm({
        ...EMPTY, ...candidate,
        phones: (candidate.phones || []).join(", "),
        skills: (candidate.skills || []).join(", "),
        certifications: (candidate.certifications || []).join(", "),
        willing_to_relocate: !!candidate.willing_to_relocate,
        willing_to_travel: !!candidate.willing_to_travel,
        consent_date: (candidate.consent_date || "").slice(0, 10),
        years_experience: candidate.years_experience ?? "",
        current_compensation: candidate.current_compensation ?? "",
        expected_compensation: candidate.expected_compensation ?? "",
      });
    } else setForm(EMPTY);
    setDups([]);
  }, [candidate, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const checkDup = async () => {
    if (isEdit || (!form.primary_email && !form.full_name)) return;
    try {
      const { data } = await api.post("/candidates/check-duplicate", {
        primary_email: form.primary_email,
        full_name: form.full_name,
        phones: form.phones ? form.phones.split(",").map((p) => p.trim()) : [],
      });
      setDups(data.duplicates || []);
    } catch {}
  };

  const submit = async () => {
    if (!form.full_name.trim()) { toast.error("Full name is required"); return; }
    setSaving(true);
    const payload = {
      ...form,
      phones: form.phones ? form.phones.split(",").map((p) => p.trim()).filter(Boolean) : [],
      skills: form.skills ? form.skills.split(",").map((s) => s.trim()).filter(Boolean) : [],
      emails: form.primary_email ? [form.primary_email] : [],
      years_experience: form.years_experience ? Number(form.years_experience) : null,
      current_compensation: form.current_compensation ? Number(form.current_compensation) : null,
      expected_compensation: form.expected_compensation ? Number(form.expected_compensation) : null,
      certifications: form.certifications ? form.certifications.split(",").map((c) => c.trim()).filter(Boolean) : [],
    };
    try {
      const res = isEdit
        ? await api.put(`/candidates/${candidate.id}`, payload)
        : await api.post("/candidates", payload);
      toast.success(isEdit ? "Candidate updated" : "Candidate created");
      onSaved?.(res.data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto scrollbar-thin" data-testid="candidate-form-sheet">
        <SheetHeader>
          <SheetTitle className="font-display">{isEdit ? "Edit Candidate" : "New Candidate"}</SheetTitle>
          <SheetDescription>Enter candidate details. Fields marked required must be completed.</SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full Name *"><Input data-testid="cf-full-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} onBlur={checkDup} /></Field>
            <Field label="Email"><Input data-testid="cf-email" type="email" value={form.primary_email} onChange={(e) => set("primary_email", e.target.value)} onBlur={checkDup} /></Field>
          </div>

          {dups.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm">
              <div className="flex items-center gap-2 font-medium text-amber-700 dark:text-amber-400"><AlertTriangle className="h-4 w-4" /> Possible duplicate</div>
              <ul className="mt-1 text-xs text-amber-700/80 dark:text-amber-400/80">
                {dups.map((d, i) => <li key={d.id || i}>{d.id ? `${d.full_name} — ${d.primary_email || "no email"}` : "Candidate already exists"}{d.owner_name ? ` — owner: ${d.owner_name}` : ""}{(d.match_on || []).length ? ` · matched on ${d.match_on.join(", ")}` : ""}</li>)}
              </ul>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Phones (comma separated)"><Input data-testid="cf-phones" value={form.phones} onChange={(e) => set("phones", e.target.value)} /></Field>
            <Field label="Current Title"><Input data-testid="cf-title" value={form.current_title} onChange={(e) => set("current_title", e.target.value)} /></Field>
            <Field label="Current Employer"><Input value={form.current_employer} onChange={(e) => set("current_employer", e.target.value)} /></Field>
            <Field label="Location"><Input value={form.location} onChange={(e) => set("location", e.target.value)} /></Field>
            <Field label="Region">
              <Select value={form.region} onValueChange={(v) => { set("region", v); set("currency", REGION_CCY[v]); }}>
                <SelectTrigger data-testid="cf-region"><SelectValue /></SelectTrigger>
                <SelectContent>{REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="cf-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Years Experience"><Input type="number" value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)} /></Field>
            <Field label="Work Authorization"><Input value={form.work_authorization} onChange={(e) => set("work_authorization", e.target.value)} /></Field>
            <Field label="Current Comp"><Input type="number" value={form.current_compensation} onChange={(e) => set("current_compensation", e.target.value)} /></Field>
            <Field label="Expected Comp"><Input type="number" value={form.expected_compensation} onChange={(e) => set("expected_compensation", e.target.value)} /></Field>
            <Field label="Source"><Input value={form.source} onChange={(e) => set("source", e.target.value)} /></Field>
            <Field label="Notice Period"><Input data-testid="cf-notice" value={form.notice_period} onChange={(e) => set("notice_period", e.target.value)} /></Field>
            <Field label="Availability"><Input data-testid="cf-availability" value={form.availability} onChange={(e) => set("availability", e.target.value)} /></Field>
            <Field label="Interview Availability"><Input data-testid="cf-interview-availability" value={form.interview_availability} onChange={(e) => set("interview_availability", e.target.value)} /></Field>
            <Field label="Time Zone"><Input data-testid="cf-timezone" value={form.time_zone} onChange={(e) => set("time_zone", e.target.value)} /></Field>
            <Field label="Certifications (comma separated)"><Input value={form.certifications} onChange={(e) => set("certifications", e.target.value)} /></Field>
            <Field label="Consent">
              <Select value={form.consent_status} onValueChange={(v) => set("consent_status", v)}>
                <SelectTrigger data-testid="cf-consent"><SelectValue /></SelectTrigger>
                <SelectContent>{["pending", "granted", "declined", "withdrawn"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Consent Date"><Input type="date" value={form.consent_date} onChange={(e) => set("consent_date", e.target.value)} /></Field>
            <Field label="Consent Method"><Input value={form.consent_method} onChange={(e) => set("consent_method", e.target.value)} /></Field>
          </div>
          <Field label="Skills (comma separated)"><Input data-testid="cf-skills" value={form.skills} onChange={(e) => set("skills", e.target.value)} /></Field>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="cf-relocate" checked={form.willing_to_relocate} onChange={(e) => set("willing_to_relocate", e.target.checked)} /> Willing to relocate</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="cf-travel" checked={form.willing_to_travel} onChange={(e) => set("willing_to_travel", e.target.checked)} /> Willing to travel</label>
          </div>
          <Field label="Screening Notes"><Textarea rows={3} value={form.screening_notes} onChange={(e) => set("screening_notes", e.target.value)} /></Field>
        </div>

        <div className="flex gap-3 mt-6 pb-6">
          <Button variant="outline" className="flex-1" onClick={onClose} data-testid="cf-cancel">Cancel</Button>
          <Button className="flex-1 bg-[#255058] hover:bg-[#1c3d43] text-white" onClick={submit} disabled={saving} data-testid="cf-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (isEdit ? "Save Changes" : "Create Candidate")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
}
